'use client';

import PropTypes from 'prop-types';
import Icon from '@/components/ui/AppIcon';

const CartSummary = ({ subtotal, cartItems, onProceedToCheckout }) => {
  //console.log("cartItems:" ,cartItems );
  const grandTotal = subtotal;

  // Calculate delivery estimate based on cart items (pick latest delivery date)
  const getDeliveryEstimate = () => {
  if (!cartItems || cartItems.length === 0) return "10-12 business days";

  //If deliveryDateISO exists in items, use that (best)
  const dates = cartItems
    .map((item) => item?.deliveryDateISO)
    .filter(Boolean)
    .map((d) => new Date(d))
    .filter((d) => !isNaN(d.getTime()));

  if (dates.length > 0) {
    const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

    return maxDate.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }

  // fallback: old method (handling + deliveryDays)
  const maxTotalDays = Math.max(
    ...cartItems.map((item) => (item?.handlingTime || 1) + (item?.deliveryDays || 10))
  );

  const today = new Date();
  const deliveryDate = new Date(today);
  deliveryDate.setDate(today.getDate() + maxTotalDays);

  return deliveryDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};
  
  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 sticky top-24">
      <h2 className="text-xl lg:text-2xl font-semibold text-foreground mb-6">Order Summary</h2>
      {/* Subtotal */}
      <div className="space-y-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-sm md:text-base caption text-muted-foreground">Subtotal</span>
          <span className="text-base md:text-lg font-semibold text-foreground data-text">
            ${subtotal?.toLocaleString('en-US')}
          </span>
        </div>

        {/* Price Breakdown Note */}
        <div className="bg-muted/50 rounded-md p-1">
          {/* <p className="text-xs caption text-muted-foreground leading-relaxed pb-2">
            <Icon name="InformationCircleIcon" size={10} className="inline-block mr-1 text-primary" />
            International Payment Fee of 4.5% will be charged at final checkout.
          </p> */}

          <p className="text-xs caption text-muted-foreground leading-relaxed">
            <Icon name="InformationCircleIcon" size={10} className="inline-block mr-1 text-primary" />
            This price includes shipping and product cost. Taxes and custom duties are not included and will be collected by the logistics service provider.
          </p>
        </div>

        {/* Shipping Method */}
        <div className="pt-4 border-t border-border">
          <label className="text-sm caption text-muted-foreground mb-2 block">
            Shipping Method
          </label>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <div className="flex items-center gap-2">
                <Icon name="TruckIcon" size={20} className="text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Express Shipping</p>
                  <p className="text-xs caption text-muted-foreground">Delivery by {getDeliveryEstimate()}</p>
                </div>
              </div>
              <span className="text-sm font-semibold text-success data-text">
                Free
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Grand Total */}
      <div className="pt-6 border-t-2 border-border mb-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-lg md:text-xl font-semibold text-foreground">Grand Total</span>
          <span className="text-2xl md:text-3xl font-bold text-primary data-text">
            ${grandTotal?.toLocaleString('en-US')}
          </span>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={onProceedToCheckout}
          className="w-full h-12 md:h-14 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-md transition-smooth focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 flex items-center justify-center gap-2"
        >
          <span>Proceed to Checkout</span>
          <Icon name="ArrowRightIcon" size={20} />
        </button>

        <button
          onClick={() => window.location.href = '/product-catalog'}
          className="w-full h-12 md:h-14 bg-muted hover:bg-muted/80 text-foreground font-medium rounded-md transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Continue Shopping
        </button>
      </div>
      {/* Trust Badges */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Icon name="ShieldCheckIcon" size={20} className="text-success" />
            <span className="text-xs caption text-muted-foreground">Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="TruckIcon" size={20} className="text-primary" />
            <span className="text-xs caption text-muted-foreground">Free Returns</span>
          </div>
        </div>
        <p className="text-xs caption text-center text-muted-foreground mt-3">
          Export Quality Certified
        </p>
      </div>
    </div>
  );
};

CartSummary.propTypes = {
  subtotal: PropTypes?.number?.isRequired,
  cartItems: PropTypes?.arrayOf(
    PropTypes?.shape({
      id: PropTypes?.oneOfType([PropTypes?.string, PropTypes?.number])?.isRequired,
      deliveryDays: PropTypes?.number
    })
  ),
  onProceedToCheckout: PropTypes?.func?.isRequired
};

export default CartSummary;